import React from 'react';
import { Box, Typography } from '@material-ui/core';
import Seo from 'sharedComponents/Seo';
import { useTranslation } from 'react-i18next';

import useStyles from './useStyles';

const TermsPage = () => {
  const classes = useStyles();
  const { t } = useTranslation();

  return (
    <Box className={classes.container}>
      <Seo title={t('meta.TermsPage.title')} />
      <Box className={classes.containerInside}>
        <Typography variant="h1" paragraph>
          Terms & Conditions
        </Typography>

        <Typography variant="body1" paragraph className={classes.termSection}>
          <b>Overview:</b> Thank you for choosing Bundle. For purposes of these terms and conditions,
          &quot;Bundle&quot;, &quot;we&quot;, and &quot;us&quot; refer to Bundle Marketplace Inc. and bundleloan.com.
          For purposes of these terms and conditions &quot;you&quot; or &quot;your&quot; refers to the user of
          Bundle&apos;s services. By creating an account or using the services of Bundle&apos;s site, you agree to the
          following terms and conditions which may be updated periodically. By continuing to use a Bundle account or
          Bundle services, you agree to any new or modified version of these terms and conditions as posted on
          Bundle&apos;s site. Failure to adhere to these terms and conditions may result in account suspension, account
          deactivation, and/or legal consequences, with or without prior notice or refund of any fees paid. If you do
          not agree to these terms, you should stop using the services.
        </Typography>

        <Typography variant="body1" paragraph className={classes.termSection}>
          <b>User Representations:</b> In order to use the services provided by Bundle, you represent that you:
        </Typography>
        <ul className={classes.listText}>
          <li>a. Are at least 18 years of age.</li>
          <li>b. Have not been suspended from using the services.</li>
          <li>c. Are in compliance with all federal, state, local, and other laws.</li>
          <li>
            d. Are not misrepresenting your identity in any way including but not limited to your name, contact
            information, licensing information, or affiliations.
          </li>
          <li>
            e. Will not upload invalid data, viruses, worms, malware, or other software applications and will not
            conduct any screen scraping or other automated activity in connection with your use of the services and will
            maintain reasonable and appropriate cybersecurity and antivirus software on any device you use to access the
            services.
          </li>
        </ul>

        <Typography variant="body1" paragraph className={classes.termSection}>
          <b>Loan Officer and Mortgage Company Representations:</b> In order to use the services provided by Bundle,
          loan officers and mortgage companies represent, in addition to all other representations in these Terms and
          Conditions including those contained in the &quot;User Representations&quot; section, that you:
        </Typography>
        <ul className={classes.listText}>
          <li>
            a. Are adhering to all federal, state, local, and other jurisdictional laws and regulations regarding
            lending, licensing, advisory services, fair lending, fair housing, anti-money laundering, fair credit
            reporting, and any and all other applicable financial rules, regulations, and laws.
          </li>
          <li>b. Agree to allow Bundle to validate affiliations and licensing at any time.</li>
          <li>c. Agree to allow Bundle to display your contact information. </li>
          <li>
            d. Provide accurate mortgage terms and conditions while using all of the services provided by Bundle. Bundle
            reserves the right to suspend or terminate your account for intentionally or unintentionally providing
            inaccurate or misleading information to any user of the site.
          </li>
          <li>
            e. Are not misrepresenting yourself as a mortgage shopper. Bundle reserves the right to suspend or terminate
            your account for misrepresenting yourself as a mortgage shopper.
          </li>
          <li>
            f. Will not use the services to submit mortgage quotes or bids to a mortgage shopper or to solicit
            application information from any mortgage shopper or prospective mortgage shopper other than through the
            protocols established from time to time by (and in the sole discretion of) Bundle for the conveyance of such
            information.{' '}
          </li>
        </ul>

        <Typography variant="body1" paragraph className={classes.termSection}>
          {' '}
          <b>Real Estate Agent Representations:</b> In order to use the services provided by Bundle, real estate agents
          represent, in addition to all other representations in these Terms and Conditions including those contained in
          the &quot;User Representations&quot; section, that you:
        </Typography>
        <ul className={classes.listText}>
          <li>
            a. Are adhering to all federal, state, local, and other jurisdictional laws and regulations regarding
            lending, licensing, advisory services, fair lending, fair housing, anti-money laundering, fair credit
            reporting, and any and all other applicable financial rules, regulations, and laws.
          </li>
          <li>b. Agree to allow Bundle to validate affiliations and licensing at any time.</li>
          <li>c. Agree to allow Bundle to display your contact information.</li>
        </ul>

        <Typography variant="body1" paragraph className={classes.termSection}>
          <b>Privacy:</b> Bundle collects certain personal identifying information and certain non-personal identifying
          information. For further information on how we use your information, please review our{' '}
          <a href="/privacy">
            <b>Privacy Policy</b>
          </a>
        </Typography>

        <Typography variant="body1" paragraph className={classes.termSection}>
          <b>Services, Mortgages, and other Financial Products:</b> Bundle provides certain services to its users,
          including but not limited to, educational content, calculators, communication platforms, profile pages, and
          document storage. Any information that you submit does not by itself constitute a loan application as defined
          by the Real Estate Settlement Procedures Act. All content on Bundle&apos;s site is intended for information
          purposes only. As a part of the services provided by Bundle, a participating loan officer may provide mortgage
          quotes, mortgage bids, or other terms and conditions based on the anonymous information that you provide.
          Bundle is not responsible for the accuracy of any data provided through the Bundle site, or any loan terms or
          loan conditions communicated directly or indirectly to any user of our services. Although Bundle is not
          responsible for any outcome or result based on any loan terms or loan conditions communicated directly or
          indirectly to any user of our services, Bundle reserves the right to review or validate the accuracy of such
          communications.
        </Typography>

        <Typography variant="body1" paragraph className={classes.termSection}>
          <b>Fees:</b> If you are a loan officer, you may be required to pay a fee to use some of the services provided
          by Bundle. Bundle will automatically charge the payment method on file on an ongoing and recurring basis until
          you cancel or change your subscription. Bundle reserves the right to change the fee amount or services
          provided, and by continuing to use a Bundle account or Bundle services, you agree to any new or modified fee
          amount or services. Fee changes and subscription changes will be communicated before they go into effect. All
          subscription fees will continue until they are canceled. You can change your subscription through the account
          settings page. All payments are non-refundable. You agree to allow Bundle to bill your credit card on a
          recurring basis. You may cancel your subscription at any time. Bundle uses Stripe to process your payment; you
          can find their Services Agreement here:{' '}
          <a href="https://stripe.com/ssa" target="_blank" rel="noopener noreferrer">
            <b>Stripe Services Agreement</b>
          </a>
        </Typography>

        <Typography variant="body1" paragraph className={classes.termSection}>
          {' '}
          <b>Content:</b> You may use the content on our site as long as it is properly cited, and you provide links to
          the information.
        </Typography>

        <Typography variant="body1" paragraph className={classes.termSection}>
          <b>Intellectual Property:</b> All services are owned by Bundle. The code, software, interfaces, information,
          and other elements of the services provided by Bundle are protected by intellectual property and other laws
          and are the property of Bundle. Except as expressly set forth herein, you may not reproduce, distribute,
          display, reverse engineer, or provide access to the services on third-party websites or otherwise. Certain
          services provided by Bundle are patent pending and failure to comply with these terms will be considered
          willful infringement. To the extent Bundle&apos;s services link to third-party products or services, Bundle
          makes no warranties, implied or express, as to the use of such services, and your use of such services may be
          subject to associated third-party terms of use or privacy policies. Bundle is not responsible for any losses
          or other costs arising directly or indirectly from your interactions with such third parties. You represent
          that any information you provide to Bundle belongs to you or is provided with the authorization of its owner.
        </Typography>

        <Typography variant="body1" paragraph className={classes.termSection}>
          <b>Account Termination:</b> Bundle reserves the right, at any point, to suspend deactivate, or delete your
          account.
        </Typography>

        <Typography variant="body1" paragraph className={classes.termSection}>
          <b>Indemnification:</b> You agree to indemnify, defend, and hold harmless Bundle, its affiliates, and their
          respective directors, officers, employees, and agents from any and all costs, losses, damages whatsoever and
          any claims or demands made by any third party due to or arising out of:
        </Typography>
        <ul className={classes.listText}>
          <li>a. your access to or use of the services;</li>
          <li>b. your breach of these terms and conditions;</li>
          <li>c. your violation of any law, regulation, or the rights of a third party;</li>
          <li>d. your violation of any law, regulation, or the rights of a third party;</li>

          <li>e. any materials you upload to, or otherwise make available through, the services; </li>

          <li>f. your willful misconduct; and</li>

          <li>
            g. any other party&apos;s access to and/or use of the services using your account and password. Bundle
            reserves the right, at its own expense, to assume the exclusive defense and control of any matter otherwise
            subject to indemnification by you, and in that case, you agree to cooperate with Bundle&apos;s control of
            the defense and resolution of that claim.
          </li>
        </ul>

        <Typography variant="body1" paragraph className={classes.termSection}>
          <b>No Warranties, Limitation of Liability:</b> BUNDLE PROVIDES THE SERVICES &quot;AS IS&quot; AND MAKES NO
          WARRANTIES, IMPLIED OR EXPRESS, AS TO THE SERVICES&apos; MERCHANTABILITY OR FITNESS FOR A PARTICULAR PURPOSE.
          IN NO EVENT SHALL BUNDLE BE LIABLE FOR ANY INDIRECT, CONSEQUENTIAL, SPECIAL, INCIDENTAL, OR PUNITIVE DAMAGES
          BASED ON, OR IN ANY WAY ARISING FROM, YOUR USE OF THE SERVICES, AND BUNDLE&apos;S ABSOLUTE LIABILITY IN ANY
          EVENT, WHETHER ALLEGED IN CONTRACT, TORT, OR OTHERWISE, SHALL NOT EXCEED THE GREATER OF $100 OR THE AMOUNT YOU
          HAVE PAID BUNDLE FOR USE OF THE SERVICES IN THE TWELVE (12) MONTHS PRECEDING THE EVENT GIVING RISE TO ANY SUCH
          CLAIM, EXCEPT WHERE OTHERWISE PROVIDED BY LAW.
        </Typography>

        <Typography variant="body1" paragraph className={classes.termSection}>
          <b>Choice of Law:</b> These terms and conditions are governed by the laws of the state of North Carolina and
          any dispute related to your use of the services shall be subject to the exclusive jurisdiction and venue of
          the state of North Carolina, Mecklenburg county courts.
        </Typography>

        <Typography variant="body1" paragraph className={classes.termSection}>
          <b>Licensing Information:</b>
          <br />
          <br />
          <u>NMLS Company ID</u>
          <br />
          Bundle Marketplace Inc.
          <br />
          NMLS# 1927373
          <br />
          <br />
          {/* <u>Florida</u>
          <br />
          Bundle Marketplace Inc.
          <br />
          Mortgage Broker License 69435
          <br />
          <br />
          <u>Georgia</u>
          <br />
          Bundle Marketplace Inc.
          <br />
          Mortgage Broker License MBR3411
          <br />
          <br />
          <u>North Carolina</u>
          <br />
          Bundle Marketplace Inc.
          <br />
          Mortgage Broker License B-187553
          <br />
          <br />
          <u>South Carolina</u>
          <br />
          Bundle Marketplace Inc.
          <br />
          Mortgage Broker License 1927373  */}
        </Typography>

        <Typography variant="body1" paragraph className={classes.termSection} />
      </Box>
    </Box>
  );
};

export default TermsPage;



// WEBPACK FOOTER //
// ./src/components/Terms/TermsPage.js